import { useEffect, useState } from 'react';
import Card from '../../components/card/Card';
import P from '../../components/texts/p/P';
import Image from '../../components/image/Image';
import Table from '../../components/table/table';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import OverdueStatusModal from '../../components/modals/OverdueStatusModal';
import EarlyCancellationDateModal from '../../components/dialogs/EarlyCancellationDateModal';
import EarlyCancellationConfirmModal from '../../components/dialogs/EarlyCancellationConfirmModal';
import { Toast, useToast } from '../../components/toast/toast';
import { earlyCancellationInstallment, getLoanInstallments } from '../../services/GuaranteesService';
import { getInstallmentColumns, InstallmentData } from '../../utils/installmentColumns';
import { OverdueData } from '../../utils/overdueColumns';
import { ITableData } from '../../types/interfaces';
import InstallmentRedefinitionActions from './RedefinirCuotasActions';
import InstallmentTotalsFooter, { TotalsColumnDef } from '../../components/InstallmentTotalsFooter/InstallmentTotalsFooter';

interface InstallmentPlanTabProps {
  loanId: string;
  loanData: any;
  refreshKey?: number;
  /** Disparado tras una redefinición exitosa para que el padre incremente su refreshKey */
  onPlanReplaced?: () => void;
}

const InstallmentPlanTab: React.FC<InstallmentPlanTabProps> = ({ loanId, loanData, refreshKey, onPlanReplaced }) => {
  const [installments, setInstallments] = useState<InstallmentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState<InstallmentData | null>(null);
  const [earlyInstallment, setEarlyInstallment] = useState<InstallmentData | null>(null);
  const [earlyDateModalOpen, setEarlyDateModalOpen] = useState(false);
  const [earlyConfirmOpen, setEarlyConfirmOpen] = useState(false);
  const [earlySelectedDate, setEarlySelectedDate] = useState('');
  const [earlyLoading, setEarlyLoading] = useState(false);
  const [tableData, setTableData] = useState<ITableData>({
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchInstallments();
  }, [loanId, refreshKey]);

  const fetchInstallments = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getLoanInstallments(loanId);

      if (response.success && 'data' in response) {
        const installmentsData = response.data.content || response.data || [];

        const enrichedInstallments = installmentsData.map((installment: any) => ({
          ...installment,
          installment_id: installment.id ?? installment.installment_id,
          installment_number:
            installment.installment_number ?? installment.installmentNumber,
          original_due_date:
            installment.original_due_date ?? installment.originalDueDate ?? null,
          status_type: installment.status_type ?? installment.statusType,
          due_date: installment.due_date ?? installment.dueDate,
          amortization: installment.amortization,
          interest: installment.interest,
          amount: installment.amount,
          remaining_amount:
            installment.remaining_amount ?? installment.remainingAmount,
          due_days: installment.due_days ?? installment.dueDays ?? 0,
          id: parseInt(loanId, 10),
          id_global: loanData?.id_global || '',
          type: '1',
          application_product_line_id: loanData?.application_product_line_id || 0,
          days_overdue: installment.due_days ?? installment.dueDays ?? 0,
          creditor_name: loanData?.creditor || '',
          currency: loanData?.currency || '',
          cuit: loanData?.cuit || '',
          company_name: loanData?.company_name || '',
          monetization_amount: installment.amount,
          monetization_status_type_id:
            loanData?.monetization_status_type_id ?? loanData?.monetizationStatusTypeId,
          total_installments: installmentsData.length,
          rate_type_id: loanData?.rate_type_id,
          rate_percentage: loanData?.rate_percentage,
        }));

        setInstallments(enrichedInstallments);

        if (response.data.total_elements !== undefined) {
          setTableData({
            page: response.data.page || 0,
            size: response.data.size || 10,
            totalElements: response.data.total_elements,
            totalPages: response.data.total_pages || 1,
          });
        } else {
          setTableData({
            page: 0,
            size: installmentsData.length,
            totalElements: installmentsData.length,
            totalPages: 1,
          });
        }
      } else {
        setError('No se encontraron cuotas para este préstamo');
      }
    } catch (err) {
      console.error('Error fetching installments:', err);
      setError('Error al cargar el plan de cuotas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (row: InstallmentData) => {
    setSelectedInstallment(row);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInstallment(null);
  };

  const handleModalSuccess = () => {
    fetchInstallments();
  };

  const handleEarlyCancellation = (row: InstallmentData) => {
    setEarlyInstallment(row);
    setEarlyDateModalOpen(true);
  };

  const handleEarlyContinue = (date: string) => {
    setEarlySelectedDate(date);
    setEarlyDateModalOpen(false);
    setEarlyConfirmOpen(true);
  };

  const handleEarlyConfirm = async () => {
    if (!earlyInstallment?.installment_id) return;
    setEarlyLoading(true);
    try {
      await earlyCancellationInstallment(earlyInstallment.installment_id, earlySelectedDate);
      useToast.success({
        title: 'Cancelación anticipada registrada',
        id: 'overdue-toast',
      });
      setEarlyConfirmOpen(false);
      setEarlyInstallment(null);
      fetchInstallments();
      onPlanReplaced?.();
    } catch (error: any) {
      useToast.error({
        title:
          error?.response?.data?.message ||
          error?.message ||
          'No se pudo registrar la cancelación anticipada',
        id: 'overdue-toast',
      });
    } finally {
      setEarlyLoading(false);
    }
  };

  const monetizationStatusTypeIdForGate =
    loanData?.monetization_status_type_id ?? loanData?.monetizationStatusTypeId;
  const earlyCancellationEnabled = Number(monetizationStatusTypeIdForGate) === 2;

  const columns = getInstallmentColumns({
    handleOpenModal,
    handleEarlyCancellation: earlyCancellationEnabled
      ? handleEarlyCancellation
      : undefined,
    earlyCancellationEnabled,
  });

  const installmentTotalsColumns: TotalsColumnDef[] = [
    { isLabel: true, minWidth: '90px', grow: 0.5 },
    { minWidth: '120px', grow: 0.7 },
    { minWidth: '120px', grow: 0.7 },
    { field: 'amortization', minWidth: '120px', grow: 0.8 },
    { field: 'interest', minWidth: '120px', grow: 0.8 },
    { field: 'amount', minWidth: '120px', grow: 0.8 },
    {
      computeTotal: (rows) => {
        const sorted = [...rows].sort(
          (a, b) =>
            (Number(a.installment_number ?? a.installmentNumber) || 0) -
            (Number(b.installment_number ?? b.installmentNumber) || 0)
        );
        const last = sorted[sorted.length - 1];
        return Number(last?.remaining_amount ?? last?.remainingAmount) || 0;
      },
      minWidth: '120px',
      grow: 0.8,
    },
    { minWidth: '100px', grow: 0.6 },
    { minWidth: '140px', grow: 0.8 },
    { minWidth: '120px', grow: 0.5 },
  ];

  const monetizationStatusTypeId = monetizationStatusTypeIdForGate;

  const handleRedefinitionSuccess = () => {
    fetchInstallments();
    onPlanReplaced?.();
  };

  const redefinitionActions = (
    <InstallmentRedefinitionActions
      loanId={loanData?.id ?? loanId}
      monetizationStatusTypeId={
        typeof monetizationStatusTypeId === 'string'
          ? parseInt(monetizationStatusTypeId, 10)
          : monetizationStatusTypeId
      }
      guaranteeType='loan'
      guaranteeNumber={loanData?.id_global ?? loanId}
      onSuccess={handleRedefinitionSuccess}
    />
  );

  if (loading) {
    return (
      <Card margin={{ top: '4' }}>
        <TableSkeleton rows={10} columns={columns.length} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        display='grid'
        justifyContent='center'
        margin={{ top: '11', bottom: '11' }}
        width='100%'
      >
        <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
          <Image name='folder_empty' />
        </Card>
        <P variant='primary' align='center' withMargin={false} weight='bold'>
          {error}
        </P>
      </Card>
    );
  }

  if (installments.length === 0) {
    return (
      <>
        {redefinitionActions}
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='100%'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No se encontraron cuotas.
          </P>
          <P variant='primary' align='center'>
            Este préstamo no tiene un plan de cuotas definido.
          </P>
        </Card>
        <Toast id='overdue-toast' />
      </>
    );
  }

  return (
    <>
      {redefinitionActions}
      <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
        <Table
          columns={columns}
          data={installments}
          tableData={tableData}
          handleChangePage={() => {}}
        />
        <InstallmentTotalsFooter
          installments={installments}
          columns={installmentTotalsColumns}
        />
      </Card>

      <OverdueStatusModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        overdueItem={selectedInstallment as OverdueData | null}
        onSuccess={handleModalSuccess}
      />

      <EarlyCancellationDateModal
        isOpen={earlyDateModalOpen}
        onClose={() => {
          setEarlyDateModalOpen(false);
          setEarlyInstallment(null);
        }}
        onContinue={handleEarlyContinue}
        maxDueDate={earlyInstallment?.due_date || ''}
        mode="installment"
        avalNumber={loanData?.id_global}
        companyName={loanData?.company_name}
        currentDueDate={earlyInstallment?.due_date}
        installmentNumber={earlyInstallment?.installment_number}
        totalInstallments={
          earlyInstallment?.total_installments || installments.length || null
        }
        installmentAmount={earlyInstallment?.amount}
      />

      <EarlyCancellationConfirmModal
        isOpen={earlyConfirmOpen}
        onClose={() => setEarlyConfirmOpen(false)}
        onConfirm={handleEarlyConfirm}
        isLoading={earlyLoading}
        selectedDate={earlySelectedDate}
        mode="installment"
        avalNumber={loanData?.id_global}
        installmentNumber={earlyInstallment?.installment_number}
        totalInstallments={
          earlyInstallment?.total_installments || installments.length || null
        }
      />

      <Toast id='overdue-toast' />
    </>
  );
};

export default InstallmentPlanTab;
